import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AddFirewallParams,
  AddFirewallResponse,
  AttachVolumeParams,
  AttachVolumeResponse,
  ConfirmResizeInstanceParams,
  ConfirmResizeInstanceResponse,
  CreateInstanceParams,
  CreateInstanceResponse,
  CreateInterfaceParams,
  CreateInterfaceResponse,
  DeleteInstanceParams,
  DeleteInstanceResponse,
  DeleteInterfaceParams,
  DeleteInterfaceResponse,
  DetachVolumeParams,
  DetachVolumeResponse,
  Flavor,
  GetFlavorParams,
  GetFlavorResponse,
  GetFlavorsParams,
  GetFlavorsResponse,
  GetImageParams,
  GetImageResponse,
  GetImagesParams,
  GetImagesResponse,
  GetInstanceActionLogsParams,
  GetInstanceActionLogsResponse,
  GetInstanceFirewallsParams,
  GetInstanceFirewallsResponse,
  GetInstanceLimitsParams,
  GetInstanceLimitsResponse,
  GetInstanceLogParams,
  GetInstanceLogResponse,
  GetInstanceMetricParams,
  GetInstanceMetricResponse,
  GetInstanceParams,
  GetInstanceResponse,
  GetInstancesParams,
  GetInstancesResponse,
  GetInterfaceParams,
  GetInterfaceResponse,
  GetInterfacesParams,
  GetInterfacesResponse,
  GetRemoteConsoleURLParams,
  GetRemoteConsoleURLResponse,
  Image,
  Instance,
  InstanceActionLogs,
  InstanceFirewall,
  InstanceLimits,
  InstanceLog,
  InstanceMetrics,
  Interface,
  RemoteConsoleURL,
  RemoveFirewallParams,
  RemoveFirewallResponse,
  ResizeInstanceParams,
  ResizeInstanceResponse,
  RestartInstanceParams,
  RevertResizeInstanceParams,
  RevertResizeInstanceResponse,
  ShelveInstanceParams,
  StartInstanceParams,
  StopInstanceParams,
  UpdateInstanceParams,
  UpdateInstanceResponse
} from "./types";

export type InstancesState = {
  instanceActionLogs: InstanceActionLogs[] | null;
  areInstanceActionLogsLoading: boolean;
  instances: Instance[] | null;
  instance: Instance | null;
  flavors: Flavor[] | null;
  flavor: Flavor | null;
  isFlavorLoading: boolean;
  areFlavorsLoading: boolean;
  isInstanceLoading: boolean;
  areInstancesLoading: boolean;
  isInstanceCreating: boolean;
  isInstanceUpdating: boolean;
  isInstanceResizing: boolean;
  isInstanceResizeConfirming: boolean;
  isInstanceResizeReverting: boolean;
  isInstanceDeleting: boolean;
  isInstanceStarting: boolean;
  isInstanceRestarting: boolean;
  isInstanceStopping: boolean;
  isInstanceShelving: boolean;
  images: Image[] | null;
  image: Image | null;
  areImagesLoading: boolean;
  isImageLoading: boolean;
  isVolumeAttaching: boolean;
  isVolumeDetaching: boolean;
  isFirewallAdding: boolean;
  isFirewallRemoving: boolean;
  interfaces: Interface[] | null;
  interface: Interface | null;
  isInterfaceLoading: boolean;
  areInterfacesLoading: boolean;
  isInterfaceCreating: boolean;
  isInterfaceDeleting: boolean;
  instanceLimits: InstanceLimits[] | null;
  areInstanceLimitsLoading: boolean;
  instanceFirewalls: InstanceFirewall[] | null;
  areInstanceFirewallsLoading: boolean;
  remoteConsoleURL: RemoteConsoleURL | null;
  isRemoteConsoleURLLoading: boolean;
  instanceLog: InstanceLog | null;
  isInstanceLogLoading: boolean;
  instanceMetrics: InstanceMetrics | null;
  areInstanceMetricsLoading: boolean;
};

const initialState: InstancesState = {
  instances: null,
  instance: null,
  flavors: null,
  flavor: null,
  isFlavorLoading: false,
  areFlavorsLoading: false,
  isInstanceLoading: false,
  areInstancesLoading: false,
  isInstanceCreating: false,
  isInstanceUpdating: false,
  isInstanceResizing: false,
  isInstanceResizeConfirming: false,
  isInstanceResizeReverting: false,
  isInstanceDeleting: false,
  isInstanceStarting: false,
  isInstanceRestarting: false,
  isInstanceStopping: false,
  isInstanceShelving: false,
  images: null,
  image: null,
  areImagesLoading: false,
  isImageLoading: false,
  isVolumeAttaching: false,
  isVolumeDetaching: false,
  isFirewallAdding: false,
  isFirewallRemoving: false,
  interfaces: null,
  interface: null,
  isInterfaceLoading: false,
  areInterfacesLoading: false,
  isInterfaceCreating: false,
  isInterfaceDeleting: false,
  instanceLimits: null,
  areInstanceLimitsLoading: false,
  instanceFirewalls: null,
  areInstanceFirewallsLoading: false,
  remoteConsoleURL: null,
  isRemoteConsoleURLLoading: false,
  instanceLog: null,
  isInstanceLogLoading: false,
  instanceActionLogs: null,
  areInstanceActionLogsLoading: false,
  instanceMetrics: null,
  areInstanceMetricsLoading: false
};

const instancesSlice = createSlice({
  name: "instances",
  initialState,
  reducers: {
    // Get Flavor
    getFlavorRequest: (state, action: PayloadAction<GetFlavorParams>) => {
      state.isFlavorLoading = true;
    },
    getFlavorSuccess: (state, action: PayloadAction<GetFlavorResponse>) => {
      state.flavor = action.payload;
      state.isFlavorLoading = false;
    },
    getFlavorFailure: (
      state,
      action: PayloadAction<{ params: GetFlavorParams; error: string }>
    ) => {
      state.isFlavorLoading = false;
    },

    // Get Flavors
    getFlavorsRequest: (state, action: PayloadAction<GetFlavorsParams>) => {
      state.areFlavorsLoading = true;
    },
    getFlavorsSuccess: (state, action: PayloadAction<GetFlavorsResponse>) => {
      state.flavors = action.payload;
      state.areFlavorsLoading = false;
    },
    getFlavorsFailure: (
      state,
      action: PayloadAction<{ params: GetFlavorsParams; error: string }>
    ) => {
      state.areFlavorsLoading = false;
    },

    // Get Instance
    getInstanceRequest: (state, action: PayloadAction<GetInstanceParams>) => {
      state.isInstanceLoading = true;
    },
    getInstanceSuccess: (state, action: PayloadAction<GetInstanceResponse>) => {
      state.instance = action.payload;
      state.isInstanceLoading = false;
    },
    getInstanceFailure: (
      state,
      action: PayloadAction<{ params: GetInstanceParams; error: string }>
    ) => {
      state.isInstanceLoading = false;
    },

    // Get Instances
    getInstancesRequest: (state, action: PayloadAction<GetInstancesParams>) => {
      state.areInstancesLoading = true;
    },
    getInstancesSuccess: (
      state,
      action: PayloadAction<GetInstancesResponse>
    ) => {
      state.instances = action.payload;
      state.areInstancesLoading = false;
    },
    getInstancesFailure: (
      state,
      action: PayloadAction<{ params: GetInstancesParams; error: string }>
    ) => {
      state.areInstancesLoading = false;
    },

    // Create Instance
    createInstanceRequest: (
      state,
      action: PayloadAction<CreateInstanceParams>
    ) => {
      state.isInstanceCreating = true;
    },
    createInstanceSuccess: (
      state,
      action: PayloadAction<CreateInstanceResponse>
    ) => {
      state.isInstanceCreating = false;
    },
    createInstanceFailure: (
      state,
      action: PayloadAction<{ params: CreateInstanceParams; error: string }>
    ) => {
      state.isInstanceCreating = false;
    },

    // Update Instance
    updateInstanceRequest: (
      state,
      action: PayloadAction<UpdateInstanceParams>
    ) => {
      state.isInstanceUpdating = true;
    },
    updateInstanceSuccess: (
      state,
      action: PayloadAction<UpdateInstanceResponse>
    ) => {
      state.isInstanceUpdating = false;
    },
    updateInstanceFailure: (
      state,
      action: PayloadAction<{ params: UpdateInstanceParams; error: string }>
    ) => {
      state.isInstanceUpdating = false;
    },

    // Resize Instance
    resizeInstanceRequest: (
      state,
      action: PayloadAction<ResizeInstanceParams>
    ) => {
      state.isInstanceResizing = true;
    },
    resizeInstanceSuccess: (
      state,
      action: PayloadAction<ResizeInstanceResponse>
    ) => {
      state.isInstanceResizing = false;
    },
    resizeInstanceFailure: (
      state,
      action: PayloadAction<{ params: ResizeInstanceParams; error: string }>
    ) => {
      state.isInstanceResizing = false;
    },

    // Confirm Resize Instance
    confirmResizeInstanceRequest: (
      state,
      action: PayloadAction<ConfirmResizeInstanceParams>
    ) => {
      state.isInstanceResizeConfirming = true;
    },
    confirmResizeInstanceSuccess: (
      state,
      action: PayloadAction<ConfirmResizeInstanceResponse>
    ) => {
      state.isInstanceResizeConfirming = false;
    },
    confirmResizeInstanceFailure: (
      state,
      action: PayloadAction<{
        params: ConfirmResizeInstanceParams;
        error: string;
      }>
    ) => {
      state.isInstanceResizeConfirming = false;
    },

    // Revert Resize Instance
    revertResizeInstanceRequest: (
      state,
      action: PayloadAction<RevertResizeInstanceParams>
    ) => {
      state.isInstanceResizeReverting = true;
    },
    revertResizeInstanceSuccess: (
      state,
      action: PayloadAction<RevertResizeInstanceResponse>
    ) => {
      state.isInstanceResizeReverting = false;
    },
    revertResizeInstanceFailure: (
      state,
      action: PayloadAction<{
        params: RevertResizeInstanceParams;
        error: string;
      }>
    ) => {
      state.isInstanceResizeReverting = false;
    },

    // Delete Instance
    deleteInstanceRequest: (
      state,
      action: PayloadAction<DeleteInstanceParams>
    ) => {
      state.isInstanceDeleting = true;
    },
    deleteInstanceSuccess: (
      state,
      action: PayloadAction<DeleteInstanceResponse>
    ) => {
      state.isInstanceDeleting = false;
      state.instance = null;
    },
    deleteInstanceFailure: (
      state,
      action: PayloadAction<{ params: DeleteInstanceParams; error: string }>
    ) => {
      state.isInstanceDeleting = false;
    },

    // Start Instance
    startInstanceRequest: (
      state,
      action: PayloadAction<StartInstanceParams>
    ) => {
      state.isInstanceStarting = true;
    },
    startInstanceSuccess: (state) => {
      state.isInstanceStarting = false;
    },
    startInstanceFailure: (
      state,
      action: PayloadAction<{ params: StartInstanceParams; error: string }>
    ) => {
      state.isInstanceStarting = false;
    },

    // Restart Instance
    restartInstanceRequest: (
      state,
      action: PayloadAction<RestartInstanceParams>
    ) => {
      state.isInstanceRestarting = true;
    },
    restartInstanceSuccess: (state) => {
      state.isInstanceRestarting = false;
    },
    restartInstanceFailure: (
      state,
      action: PayloadAction<{ params: RestartInstanceParams; error: string }>
    ) => {
      state.isInstanceRestarting = false;
    },

    // Stop Instance
    stopInstanceRequest: (state, action: PayloadAction<StopInstanceParams>) => {
      state.isInstanceStopping = true;
    },
    stopInstanceSuccess: (state) => {
      state.isInstanceStopping = false;
    },
    stopInstanceFailure: (
      state,
      action: PayloadAction<{ params: StopInstanceParams; error: string }>
    ) => {
      state.isInstanceStopping = false;
    },

    // Shelve Instance
    shelveInstanceRequest: (
      state,
      action: PayloadAction<ShelveInstanceParams>
    ) => {
      state.isInstanceShelving = true;
    },
    shelveInstanceSuccess: (state) => {
      state.isInstanceShelving = false;
    },
    shelveInstanceFailure: (
      state,
      action: PayloadAction<{ params: ShelveInstanceParams; error: string }>
    ) => {
      state.isInstanceShelving = false;
    },

    // Get Images
    getImagesRequest: (state, action: PayloadAction<GetImagesParams>) => {
      state.areImagesLoading = true;
    },
    getImagesSuccess: (state, action: PayloadAction<GetImagesResponse>) => {
      state.images = action.payload;
      state.areImagesLoading = false;
    },
    getImagesFailure: (
      state,
      action: PayloadAction<{ params: GetImagesParams; error: string }>
    ) => {
      state.areImagesLoading = false;
    },

    // Get Image
    getImageRequest: (state, action: PayloadAction<GetImageParams>) => {
      state.isImageLoading = true;
    },
    getImageSuccess: (state, action: PayloadAction<GetImageResponse>) => {
      state.image = action.payload;
      state.isImageLoading = false;
    },
    getImageFailure: (
      state,
      action: PayloadAction<{ params: GetImageParams; error: string }>
    ) => {
      state.isImageLoading = false;
    },

    // Attach Volume
    attachVolumeRequest: (state, action: PayloadAction<AttachVolumeParams>) => {
      state.isVolumeAttaching = true;
    },
    attachVolumeSuccess: (
      state,
      action: PayloadAction<AttachVolumeResponse>
    ) => {
      state.isVolumeAttaching = false;
    },
    attachVolumeFailure: (
      state,
      action: PayloadAction<{ params: AttachVolumeParams; error: string }>
    ) => {
      state.isVolumeAttaching = false;
    },

    // Detach Volume
    detachVolumeRequest: (state, action: PayloadAction<DetachVolumeParams>) => {
      state.isVolumeDetaching = true;
    },
    detachVolumeSuccess: (
      state,
      action: PayloadAction<DetachVolumeResponse>
    ) => {
      state.isVolumeDetaching = false;
    },
    detachVolumeFailure: (
      state,
      action: PayloadAction<{ params: DetachVolumeParams; error: string }>
    ) => {
      state.isVolumeDetaching = false;
    },

    // Add Firewall
    addFirewallRequest: (state, action: PayloadAction<AddFirewallParams>) => {
      state.isFirewallAdding = true;
    },
    addFirewallSuccess: (state, action: PayloadAction<AddFirewallResponse>) => {
      state.isFirewallAdding = false;
    },
    addFirewallFailure: (
      state,
      action: PayloadAction<{ params: AddFirewallParams; error: string }>
    ) => {
      state.isFirewallAdding = false;
    },

    // Remove Firewall
    removeFirewallRequest: (
      state,
      action: PayloadAction<RemoveFirewallParams>
    ) => {
      state.isFirewallRemoving = true;
    },
    removeFirewallSuccess: (
      state,
      action: PayloadAction<RemoveFirewallResponse>
    ) => {
      state.isFirewallRemoving = false;
    },
    removeFirewallFailure: (
      state,
      action: PayloadAction<{ params: RemoveFirewallParams; error: string }>
    ) => {
      state.isFirewallRemoving = false;
    },

    // Get Interface
    getInterfaceRequest: (state, action: PayloadAction<GetInterfaceParams>) => {
      state.isInterfaceLoading = true;
    },
    getInterfaceSuccess: (
      state,
      action: PayloadAction<GetInterfaceResponse>
    ) => {
      state.interface = action.payload;
      state.isInterfaceLoading = false;
    },
    getInterfaceFailure: (
      state,
      action: PayloadAction<{ params: GetInterfaceParams; error: string }>
    ) => {
      state.isInterfaceLoading = false;
    },

    // Get Interfaces
    getInterfacesRequest: (
      state,
      action: PayloadAction<GetInterfacesParams>
    ) => {
      state.areInterfacesLoading = true;
    },
    getInterfacesSuccess: (
      state,
      action: PayloadAction<GetInterfacesResponse>
    ) => {
      state.interfaces = action.payload;
      state.areInterfacesLoading = false;
    },
    getInterfacesFailure: (
      state,
      action: PayloadAction<{ params: GetInterfacesParams; error: string }>
    ) => {
      state.areInterfacesLoading = false;
    },

    // Create Interface
    createInterfaceRequest: (
      state,
      action: PayloadAction<CreateInterfaceParams>
    ) => {
      state.isInterfaceCreating = true;
    },
    createInterfaceSuccess: (
      state,
      action: PayloadAction<CreateInterfaceResponse>
    ) => {
      state.isInterfaceCreating = false;
    },
    createInterfaceFailure: (
      state,
      action: PayloadAction<{ params: CreateInterfaceParams; error: string }>
    ) => {
      state.isInterfaceCreating = false;
    },

    // Delete Interface
    deleteInterfaceRequest: (
      state,
      action: PayloadAction<DeleteInterfaceParams>
    ) => {
      state.isInterfaceDeleting = true;
    },
    deleteInterfaceSuccess: (
      state,
      action: PayloadAction<DeleteInterfaceResponse>
    ) => {
      state.isInterfaceDeleting = false;
    },
    deleteInterfaceFailure: (
      state,
      action: PayloadAction<{ params: DeleteInterfaceParams; error: string }>
    ) => {
      state.isInterfaceDeleting = false;
    },

    // Clear Interfaces
    clearInterfaces: (state) => {
      state.interfaces = initialState.interfaces;
    },

    // Get Instance Limits
    getInstanceLimitsRequest: (
      state,
      action: PayloadAction<GetInstanceLimitsParams>
    ) => {
      state.areInstanceLimitsLoading = true;
    },
    getInstanceLimitsSuccess: (
      state,
      action: PayloadAction<GetInstanceLimitsResponse>
    ) => {
      state.instanceLimits = action.payload;
      state.areInstanceLimitsLoading = false;
    },
    getInstanceLimitsFailure: (
      state,
      action: PayloadAction<{ params: GetInstanceLimitsParams; error: string }>
    ) => {
      state.areInstanceLimitsLoading = false;
    },

    // Get Instance Firewalls
    getInstanceFirewallsRequest: (
      state,
      action: PayloadAction<GetInstanceFirewallsParams>
    ) => {
      state.areInstanceFirewallsLoading = true;
    },
    getInstanceFirewallsSuccess: (
      state,
      action: PayloadAction<GetInstanceFirewallsResponse>
    ) => {
      state.instanceFirewalls = action.payload;
      state.areInstanceFirewallsLoading = false;
    },
    getInstanceFirewallsFailure: (
      state,
      action: PayloadAction<{
        params: GetInstanceFirewallsParams;
        error: string;
      }>
    ) => {
      state.areInstanceFirewallsLoading = false;
    },

    // Clear Instance Firewalls
    clearInstanceFirewalls: (state) => {
      state.instanceFirewalls = initialState.instanceFirewalls;
    },

    // Get Remote Console URL
    getRemoteConsoleURLRequest: (
      state,
      action: PayloadAction<GetRemoteConsoleURLParams>
    ) => {
      state.isRemoteConsoleURLLoading = true;
    },
    getRemoteConsoleURLSuccess: (
      state,
      action: PayloadAction<GetRemoteConsoleURLResponse>
    ) => {
      state.remoteConsoleURL = action.payload;
      state.isRemoteConsoleURLLoading = false;
    },
    getRemoteConsoleURLFailure: (
      state,
      action: PayloadAction<{
        params: GetRemoteConsoleURLParams;
        error: string;
      }>
    ) => {
      state.isRemoteConsoleURLLoading = false;
    },

    // Get Instance Log
    getInstanceLogRequest: (
      state,
      action: PayloadAction<GetInstanceLogParams>
    ) => {
      state.isInstanceLogLoading = true;
    },
    getInstanceLogSuccess: (
      state,
      action: PayloadAction<GetInstanceLogResponse>
    ) => {
      state.instanceLog = action.payload;
      state.isInstanceLogLoading = false;
    },
    getInstanceLogFailure: (
      state,
      action: PayloadAction<{ params: GetInstanceLogParams; error: string }>
    ) => {
      state.isInstanceLogLoading = false;
    },

    // Get Instance Metrics
    getInstanceMetricsRequest: (
      state,
      action: PayloadAction<GetInstanceMetricParams>
    ) => {
      state.areInstanceMetricsLoading = true;
    },
    getInstanceMetricsSuccess: (
      state,
      action: PayloadAction<GetInstanceMetricResponse>
    ) => {
      state.instanceMetrics = action.payload;
      state.areInstanceMetricsLoading = false;
    },
    getInstanceMetricsFailure: (
      state,
      action: PayloadAction<{ params: GetInstanceMetricParams; error: string }>
    ) => {
      state.areInstanceMetricsLoading = false;
    },

    // Get Instance Action Logs
    getInstanceActionLogsRequest: (
      state,
      action: PayloadAction<GetInstanceActionLogsParams>
    ) => {
      state.areInstanceActionLogsLoading = true;
    },
    getInstanceActionLogsSuccess: (
      state,
      action: PayloadAction<GetInstanceActionLogsResponse>
    ) => {
      state.instanceActionLogs = action.payload;
      state.areInstanceActionLogsLoading = false;
    },
    getInstanceActionLogsFailure: (
      state,
      action: PayloadAction<{
        params: GetInstanceActionLogsParams;
        error: string;
      }>
    ) => {
      state.areInstanceActionLogsLoading = false;
    },

    // Clear
    clear: () => initialState
  }
});

export const {
  getFlavorRequest,
  getFlavorSuccess,
  getFlavorFailure,
  getFlavorsRequest,
  getFlavorsSuccess,
  getFlavorsFailure,
  getInstanceRequest,
  getInstanceSuccess,
  getInstanceFailure,
  getInstancesRequest,
  getInstancesSuccess,
  getInstancesFailure,
  createInstanceRequest,
  createInstanceSuccess,
  createInstanceFailure,
  updateInstanceRequest,
  updateInstanceSuccess,
  updateInstanceFailure,
  resizeInstanceRequest,
  resizeInstanceSuccess,
  resizeInstanceFailure,
  confirmResizeInstanceRequest,
  confirmResizeInstanceSuccess,
  confirmResizeInstanceFailure,
  revertResizeInstanceRequest,
  revertResizeInstanceSuccess,
  revertResizeInstanceFailure,
  deleteInstanceRequest,
  deleteInstanceSuccess,
  deleteInstanceFailure,
  startInstanceRequest,
  startInstanceSuccess,
  startInstanceFailure,
  restartInstanceRequest,
  restartInstanceSuccess,
  restartInstanceFailure,
  stopInstanceRequest,
  stopInstanceSuccess,
  stopInstanceFailure,
  shelveInstanceRequest,
  shelveInstanceSuccess,
  shelveInstanceFailure,
  getImagesRequest,
  getImagesSuccess,
  getImagesFailure,
  getImageRequest,
  getImageSuccess,
  getImageFailure,
  attachVolumeRequest,
  attachVolumeSuccess,
  attachVolumeFailure,
  detachVolumeRequest,
  detachVolumeSuccess,
  detachVolumeFailure,
  addFirewallRequest,
  addFirewallSuccess,
  addFirewallFailure,
  removeFirewallRequest,
  removeFirewallSuccess,
  removeFirewallFailure,
  getInterfaceRequest,
  getInterfaceSuccess,
  getInterfaceFailure,
  getInterfacesRequest,
  getInterfacesSuccess,
  getInterfacesFailure,
  createInterfaceRequest,
  createInterfaceSuccess,
  createInterfaceFailure,
  deleteInterfaceRequest,
  deleteInterfaceSuccess,
  deleteInterfaceFailure,
  clearInterfaces,
  getInstanceLimitsRequest,
  getInstanceLimitsSuccess,
  getInstanceLimitsFailure,
  getInstanceFirewallsRequest,
  getInstanceFirewallsSuccess,
  getInstanceFirewallsFailure,
  clearInstanceFirewalls,
  getRemoteConsoleURLRequest,
  getRemoteConsoleURLSuccess,
  getRemoteConsoleURLFailure,
  getInstanceLogRequest,
  getInstanceLogSuccess,
  getInstanceLogFailure,
  getInstanceMetricsRequest,
  getInstanceMetricsSuccess,
  getInstanceMetricsFailure,
  getInstanceActionLogsRequest,
  getInstanceActionLogsSuccess,
  getInstanceActionLogsFailure,
  clear
} = instancesSlice.actions;

export const { actions: instancesActions } = instancesSlice;

export const reducer = instancesSlice.reducer;
